import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useNavigate, useMatch } from 'react-router-dom';

import FilledButton from '../../../components/Buttons/FilledButton';
import Th from '../../../components/Th';
import PermissionManager from '../../../components/PermissionManager';
import Pagination from '../../../components/Pagination';
import ReportTabs from '../../../components/Reports/ReportTabs';
import SearchBar from '../../../components/SearchBar';
import {
  RESOURCE_TYPES,
  getRoleDisplayName,
  getRoleHint,
} from '../../../services/permissions/constants';
import { ORG_ROLES } from '../../../constants/main';
import getUserPhotoUrl from '../../../helpers/getUserPhotoUrl';
import UserService from '../../../services/UserService';
import PermissionService from '../../../services/PermissionService';
import OrganizationService from '../../../services/OrganizationService';
import { UiContext } from '../../../context/UiContext';
import userIcon from '../../../assets/images/usericon.webp';
import RoundedInput from '../../../components/Inputs/RoundedInput';
import classes from './styles.module.scss';

const tableHeaders = [
  {
    field: 'name',
    title: 'Name',
  },
  {
    field: 'email',
    title: 'Email',
  },
  {
    field: 'title',
    title: 'Title',
  },
  {
    field: 'role',
    title: 'Role',
  },
  {
    field: 'actions',
    title: 'Actions',
    noSort: true,
  },
];

export default function UserManagementPage() {
  const [searchInput, setSearchInput] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [activePage, setActivePage] = useState(1);
  const tabs = ['Active', 'Inactive'];
  const [currentTab, setCurrentTab] = useState(tabs[0]);
  const itemsPerPage = 10;
  const [folderPage, setFolderPage] = useState(1);
  const [meetingPage, setMeetingPage] = useState(1);
  const PERMISSION_ITEMS_PER_PAGE = 6;
  const [isPermissionManagerVisible, setIsPermissionManagerVisible] =
    useState(false);
  const navigate = useNavigate();
  const permissionsMatch = useMatch(
    '/organization-settings/user-management/:userId/permissions'
  );
  const editMatch = useMatch(
    '/organization-settings/user-management/:userId/edit'
  );
  const selectedUserId = permissionsMatch?.params?.userId || editMatch?.params?.userId || null;
  const isViewingPermissions = !!permissionsMatch?.params?.userId;
  const isEditingUser = !!editMatch?.params?.userId;
  const { notifyError, notifySuccess, showConfirmActionModal } = useContext(UiContext);

  const { data: currentUser } = useQuery('me', UserService.getMe);

  // Get organization ID from current user
  const organizationId = currentUser?.organizationId;

  // Check if user can manage (must be org owner)
  const canManage = useMemo(() => {
    if (!currentUser) return false;
    return currentUser.orgRole?.access === ORG_ROLES.OWNER;
  }, [currentUser]);

  // Fetch organization users
  const {
    data: users = [],
    isLoading,
    refetch,
  } = useQuery(
    [
      'organizationUsers',
      organizationId,
      searchInput,
      sortField,
      sortOrder,
      activePage,
      'includeInactive',
    ],
    () =>
      PermissionService.getUsers(organizationId, RESOURCE_TYPES.ORGANIZATION, {
        includeInactive: true,
      }),
    {
      enabled: !!organizationId && canManage,
      keepPreviousData: true,
    }
  );

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users;

    // Apply search filter
    if (searchInput) {
      const searchLower = searchInput.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.user?.name?.toLowerCase().includes(searchLower) ||
          user.user?.email?.toLowerCase().includes(searchLower) ||
          user.user?.title?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue;
      let bValue;

      switch (sortField) {
        case 'name':
          aValue = a.user?.name || '';
          bValue = b.user?.name || '';
          break;
        case 'email':
          aValue = a.user?.email || '';
          bValue = b.user?.email || '';
          break;
        case 'title':
          aValue = a.user?.title || '';
          bValue = b.user?.title || '';
          break;
        case 'role':
          aValue = a.role || '';
          bValue = b.role || '';
          break;
        default:
          return 0;
      }

      if (sortOrder === 'ASC') {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });

    return sorted;
  }, [users, searchInput, sortField, sortOrder]);

  const handleFolderPageChange = (page) => {
    setFolderPage(page);
  };

  const handleMeetingPageChange = (page) => {
    setMeetingPage(page);
  };

  const paginatedUsers = useMemo(() => {
    const startIndex = (activePage - 1) * itemsPerPage;
    const filteredByTab = filteredAndSortedUsers.filter((user) =>
      currentTab === 'Active'
        ? user.user?.isActive !== false
        : user.user?.isActive === false
    );
    return filteredByTab.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedUsers, activePage, currentTab]);

  const tabFilteredCount = useMemo(
    () =>
      filteredAndSortedUsers.filter((user) =>
        currentTab === 'Active'
          ? user.user?.isActive !== false
          : user.user?.isActive === false
      ).length,
    [filteredAndSortedUsers, currentTab]
  );

  const emptyMessage = useMemo(() => {
    if (searchInput) return 'No users found matching your search.';
    if (currentTab === 'Active') {
      return 'No active users in this organization yet.';
    }
    return 'No inactive users.';
  }, [currentTab, searchInput]);


  useEffect(() => {
    setFolderPage(1);
    setMeetingPage(1);
  }, [selectedUserId]);

  const selectedUser = useMemo(() => {
    if (!selectedUserId) {
      return null;
    }
    return users.find(
      (permission) => String(permission.userId) === String(selectedUserId)
    );
  }, [users, selectedUserId]);

  const {
    data: selectedUserPermissions = null,
    isLoading: isSelectedUserPermissionsLoading,
    error: selectedUserPermissionsError,
  } = useQuery(
    ['organizationUserPermissions', organizationId, selectedUserId],
    () =>
      UserService.getOrganizationUserPermissions({
        organizationId,
        userId: selectedUserId,
      }),
    {
      enabled: !!organizationId && !!selectedUserId && canManage,
    }
  );

  // Update role mutation
  const { mutate: updateRole } = useMutation(
    async ({ userId, role }) => {
      return PermissionService.updateRole(
        organizationId,
        RESOURCE_TYPES.ORGANIZATION,
        userId,
        role
      );
    },
    {
      onSuccess: () => {
        refetch();
        notifySuccess('User role updated successfully');
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to update role'
        );
      },
    }
  );

  // Remove user mutation
  const { mutate: removeUser } = useMutation(
    async ({ userId }) => {
      return PermissionService.removeUser(
        organizationId,
        RESOURCE_TYPES.ORGANIZATION,
        userId
      );
    },
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to remove user'
        );
      },
    }
  );

  const { mutate: reactivateUser, isLoading: isReactivating } = useMutation(
    async ({ userId }) => {
      return PermissionService.reactivateUser(
        organizationId,
        RESOURCE_TYPES.ORGANIZATION,
        userId
      );
    },
    {
      onSuccess: () => {
        refetch();
        notifySuccess('User reactivated.');
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to reactivate user'
        );
      },
    }
  );

  const { mutate: resendInvitation, isLoading: isResendingInvitation } =
    useMutation(
      async ({ userId }) => {
        return PermissionService.resendInvitation(
          organizationId,
          RESOURCE_TYPES.ORGANIZATION,
          userId
        );
      },
      {
        onSuccess: (data) => {
          notifySuccess(
            data?.email
              ? `Invitation resent to ${data.email}.`
              : 'Invitation resent.'
          );
        },
        onError: (error) => {
          notifyError(
            error?.response?.data?.message ||
              error?.message ||
              'Failed to resend invitation'
          );
        },
      }
    );

  const handleResendInvitation = (userId, userName, userEmail) => {
    showConfirmActionModal({
      title: 'Resend Invitation?',
      message: `Are you sure you want to resend the invitation to ${
        userName || 'this user'
      }${userEmail ? ` (${userEmail})` : ''}?<br><div style="padding: 16px; margin-top: 16px; font-size: 16px; text-align: left;"><div style="margin-bottom: 4px;">• A new invitation email will be sent to the user.</div><div>• Any previous invitation link they may have received will no longer work.</div></div>`,
      onConfirm: () => {
        resendInvitation({ userId });
      },
    });
  };

  const handleRoleChange = (userId, newRole) => {
    updateRole({ userId, role: newRole });
  };

  const handleRemoveUser = (userId, userName) => {
    showConfirmActionModal({
      title: 'Deactivate User?',
      message: `Are you sure you want to remove ${userName} from the organization?<br><div style="padding: 16px; margin-top: 16px; font-size: 16px; text-align: left;"><div style="margin-bottom: 4px;">• The user will no longer have access to the platform.</div><div>• All the content this user created or contributed will be preserved.</div></div>`,
      onConfirm: () => {
        removeUser({ userId });
      },
    });
  };

  const handleRowClick = (userId) => {
    if (!canManage || isEditingUser) {
      return;
    }
    navigate(`/organization-settings/user-management/${userId}/permissions`);
  };

  const handleEditClick = (userId, event) => {
    event.stopPropagation();
    navigate(`/organization-settings/user-management/${userId}/edit`);
  };

  const handleBackToList = () => {
    navigate('/organization-settings/user-management');
  };

  const describeRole = (resourceType, role) => {
    if (!role) {
      return 'No access assigned yet.';
    }
    return (
      getRoleHint(resourceType, role) ||
      `Has ${getRoleDisplayName(role, resourceType)} access`
    );
  };

  const renderRoleBadge = (role, variant = 'default', resourceType = RESOURCE_TYPES.ORGANIZATION) => (
    <span
      className={`${classes.permissionBadge} ${
        variant === 'primary' ? classes.permissionBadgePrimary : ''
      }`}
    >
      {role ? getRoleDisplayName(role, resourceType) : 'No role assigned'}
    </span>
  );

  const renderPermissionsGroup = (
    title,
    items,
    resourceType,
    { highlightOwner = false, emptyLabel, infoBanner },
    pagination
  ) => (
    <div className={classes.permissionGroup}>
      <div className={classes.permissionGroupHeader}>
        <h3>{title}</h3>
        {items?.length > 0 && (
          <span className={classes.permissionGroupCount}>
            {items.length} {items.length === 1 ? 'entry' : 'entries'}
          </span>
        )}
      </div>
      {infoBanner && (
        <div className={classes.permissionInfoBanner}>
          {infoBanner}
        </div>
      )}
      {!items || items.length === 0 ? (
        <p className={classes.permissionsEmpty}>{emptyLabel}</p>
      ) : (
        <>
          <ul className={classes.permissionItems}>
            {(pagination
              ? items.slice(
                  (pagination.page - 1) * PERMISSION_ITEMS_PER_PAGE,
                  pagination.page * PERMISSION_ITEMS_PER_PAGE
                )
              : items
            ).map((item) => (
              <li
                key={`${resourceType}-${item.id || item.name || 'entry'}`}
                className={classes.permissionItem}
              >
                <div className={classes.permissionItemDetails}>
                  <span className={classes.permissionResourceName}>
                    {item.name || 'Untitled'}
                  </span>
                  <p className={classes.permissionHint}>
                    {describeRole(resourceType, item.role)}
                  </p>
                </div>
                {renderRoleBadge(
                  item.role,
                  highlightOwner && item.role === 'owner' ? 'primary' : 'default',
                  resourceType
                )}
              </li>
            ))}
          </ul>
          {pagination && items.length > PERMISSION_ITEMS_PER_PAGE && (
            <div className={classes.permissionsPagination}>
              <Pagination
                activePage={pagination.page}
                itemsCountPerPage={PERMISSION_ITEMS_PER_PAGE}
                totalItemsCount={items.length}
                pageRangeDisplayed={5}
                onChange={pagination.handleChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  // Fetch organization roles for edit form
  const {
    data: organizationRoles = [],
    isLoading: isLoadingRoles,
  } = useQuery(
    ['organizationRoles', organizationId],
    () => OrganizationService.getOrganizationRoles(organizationId),
    {
      enabled: !!organizationId && isEditingUser && canManage,
    }
  );

  // Edit user form state
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    title: '',
    orgRoleId: '',
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // Initialize form data when user is selected
  useEffect(() => {
    if (isEditingUser && selectedUser) {
      // Get orgRoleId from _original user object if available
      const orgRoleId = selectedUser._original?.orgRoleId || selectedUser.orgRoleId || '';
      setEditFormData({
        name: selectedUser.user?.name || '',
        email: selectedUser.user?.email || '',
        title: selectedUser.user?.title || '',
        orgRoleId,
      });
      setEditFormErrors({});
    }
  }, [isEditingUser, selectedUser]);

  // Update user mutation
  const { mutate: updateUser, isLoading: isUpdatingUser } = useMutation(
    async (data) => {
      return OrganizationService.updateOrganizationUser({
        organizationId,
        userId: selectedUserId,
        ...data,
      });
    },
    {
      onSuccess: () => {
        refetch();
        notifySuccess('User updated successfully');
        navigate('/organization-settings/user-management');
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.errors?.[0]?.msg ||
          error?.message ||
          'Failed to update user';
        notifyError(errorMessage);
      },
    }
  );

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (editFormErrors[field]) {
      setEditFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateEditForm = () => {
    const errors = {};
    if (!editFormData.name || !editFormData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!editFormData.email || !editFormData.email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editFormData.email.trim())) {
        errors.email = 'Please enter a valid email';
      }
    }
    if (!editFormData.orgRoleId) {
      errors.orgRoleId = 'Organization role is required';
    }
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditFormSubmit = (e) => {
    e.preventDefault();
    if (!validateEditForm()) {
      return;
    }
    updateUser({
      name: editFormData.name.trim(),
      email: editFormData.email.trim(),
      title: editFormData.title.trim() || null,
      orgRoleId: editFormData.orgRoleId,
    });
  };

  const renderEditView = () => {
    if (!selectedUser) {
      return (
        <div className={classes.userInfoCard}>
          {isLoading ? (
            <div className={classes.permissionsLoading}>Loading user...</div>
          ) : (
            <div className={classes.permissionsError}>
              This user could not be found.
            </div>
          )}
        </div>
      );
    }

    const avatar =
      getUserPhotoUrl(selectedUser.user?.photoPath) || userIcon;

    return (
      <div className={classes.userInfoCard}>
        <div className={classes.userInfoHeader}>
          <div className={classes.userInfoContent}>
            <img
              src={avatar}
              alt={selectedUser.user?.name || 'User'}
              className={classes.userInfoAvatar}
            />
            <div className={classes.userInfoDetails}>
              <h2 className={classes.userInfoName}>
                {selectedUser.user?.name || 'Unknown User'}
              </h2>
              {selectedUser.user?.email && (
                <a
                  href={`mailto:${selectedUser.user.email}`}
                  className={classes.userInfoEmail}
                >
                  {selectedUser.user.email}
                </a>
              )}
              {selectedUser.user?.title && (
                <span className={classes.userInfoTitle}>
                  {selectedUser.user.title}
                </span>
              )}
            </div>
          </div>
          <div className={classes.permissionsRoleSelect}>
            {isLoadingRoles ? (
              <div className={classes.loading}>Loading roles...</div>
            ) : (
              <select
                className={`${classes.roleSelect} ${
                  editFormErrors.orgRoleId ? classes.error : ''
                }`}
                value={editFormData.orgRoleId}
                onChange={(e) =>
                  handleEditFormChange('orgRoleId', e.target.value)
                }
              >
                <option value="">Select a role</option>
                  {organizationRoles
                    .filter((role) => role.access !== ORG_ROLES.MANAGER)
                    .map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name || getRoleDisplayName(role.access, RESOURCE_TYPES.ORGANIZATION)}
                      </option>
                    ))}
              </select>
            )}
            {editFormErrors.orgRoleId && (
              <span className={classes.errorText}>
                {editFormErrors.orgRoleId}
              </span>
            )}
          </div>
        </div>

          <form onSubmit={handleEditFormSubmit} className={classes.editForm}>
            <div className={classes.editFormRow}>
              <div className={classes.editFormCol}>
                <RoundedInput
                  label={
                    editFormErrors.name
                      ? editFormErrors.name
                      : 'Full name'
                  }
                  value={editFormData.name}
                  onChange={(e) => handleEditFormChange('name', e.target.value)}
                  errors={editFormErrors.name}
                  isTouched={!!editFormErrors.name}
                  type="text"
                  style={{ height: 65 }}
                />
              </div>
              <div className={classes.editFormCol}>
                <RoundedInput
                  label={
                    editFormErrors.title
                      ? editFormErrors.title
                      : 'Title'
                  }
                  value={editFormData.title}
                  onChange={(e) => handleEditFormChange('title', e.target.value)}
                  errors={editFormErrors.title}
                  isTouched={!!editFormErrors.title}
                  type="text"
                  style={{ height: 65 }}
                />
              </div>
            </div>

            <div className={classes.editFormRow}>
              <div className={classes.editFormCol}>
                <RoundedInput
                  label={
                    editFormErrors.email
                      ? editFormErrors.email
                      : 'Email address'
                  }
                  value={editFormData.email}
                  onChange={(e) => handleEditFormChange('email', e.target.value)}
                  errors={editFormErrors.email}
                  isTouched={!!editFormErrors.email}
                  type="email"
                  style={{ height: 65 }}
                />
              </div>
            </div>

            <div className={classes.editFormActions}>
              <button
                type="button"
                className={classes.cancelFormButton}
                onClick={handleBackToList}
                disabled={isUpdatingUser}
              >
                Cancel
              </button>
              <FilledButton
                type="submit"
                width={240}
                disabled={isUpdatingUser}
              >
                {isUpdatingUser ? 'Saving...' : 'Save Changes'}
              </FilledButton>
            </div>
          </form>
      </div>
    );
  };

  const renderPermissionsView = () => {
    if (!selectedUser) {
      return (
        <div className={classes.userInfoCard}>
          {isLoading ? (
            <div className={classes.permissionsLoading}>Loading user...</div>
          ) : (
            <div className={classes.permissionsError}>
              This user could not be found.
            </div>
          )}
        </div>
      );
    }

    const avatar =
      getUserPhotoUrl(selectedUser.user?.photoPath) || userIcon;

    const selectedPermissionsData =
      selectedUserPermissions || {
        organization: null,
        folders: [],
        meetings: [],
      };

    const organizationItems = selectedPermissionsData.organization
      ? [
          {
            id: selectedPermissionsData.organization.id || 'organization',
            name:
              selectedPermissionsData.organization.name || 'Organization',
            role: selectedPermissionsData.organization.role,
          },
        ]
      : [];

    const folderItems = selectedPermissionsData.folders || [];
    const meetingItems = selectedPermissionsData.meetings || [];
    
    // Get unique roles for each resource type
    const orgRole = selectedPermissionsData.organization?.role;
    const folderRoles = [...new Set(folderItems.map((f) => f.role))];
    const meetingRoles = [...new Set(meetingItems.map((m) => m.role))];
    
    // Helper function to get organization banner text
    const getOrgBannerText = () => {
      if (!orgRole) return null;
      switch (orgRole) {
        case ORG_ROLES.OWNER:
          return (
            <div className={classes.infoBannerContent}>
              <strong>Organization Admin:</strong> Can manage all folders, videos, and users; create and delete resources; modify permissions across the organization.
            </div>
          );
        case ORG_ROLES.MANAGER:
          return (
            <div className={classes.infoBannerContent}>
              <strong>Organization Manager:</strong> Can manage folders and videos; create and edit resources within the organization.
            </div>
          );
        case ORG_ROLES.VIEWER:
          return (
            <div className={classes.infoBannerContent}>
              <strong>Organization Viewer:</strong> Can view organization resources but cannot create, edit, or delete content.
            </div>
          );
        default:
          return null;
      }
    };
    
    // Helper function to get folder banner text
    const getFolderBannerText = () => {
      if (folderRoles.length === 0) return null;
      
      const hasOwner = folderRoles.includes('owner');
      const hasContributor = folderRoles.includes('contributor');
      const hasViewer = folderRoles.includes('viewer');
      
      if (hasOwner && hasContributor && hasViewer) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Mixed Folder Access:</strong> Has owner, contributor, and viewer access across different folders. Can access, edit, and manage videos in owned/contributed folders; view-only access in viewer folders.
          </div>
        );
      }
      if (hasOwner && hasContributor) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Folder Owner/Contributor:</strong> Can access, edit, and manage all videos within these folders. Has full control over folder contents and can create new videos.
          </div>
        );
      }
      if (hasOwner) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Folder Owner:</strong> Has full control of folders and all their contents. Can access, edit, and manage all videos within these folders.
          </div>
        );
      }
      if (hasContributor && hasViewer) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Folder Contributor/Viewer:</strong> Can access, edit, and manage videos in contributed folders; view-only access in viewer folders.
          </div>
        );
      }
      if (hasContributor) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Folder Contributor:</strong> Can view, create, and publish videos in these folders. Can access and edit videos within contributed folders.
          </div>
        );
      }
      if (hasViewer) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Folder Viewer:</strong> Can view all videos within these folders. Has read-only access to folder contents.
          </div>
        );
      }
      return null;
    };
    
    // Helper function to get video banner text
    const getMeetingBannerText = () => {
      if (meetingRoles.length === 0) return null;
      
      const hasCreator = meetingRoles.includes('creator');
      const hasContributor = meetingRoles.includes('contributor');
      const hasReviewer = meetingRoles.includes('reviewer');
      const hasViewer = meetingRoles.includes('viewer');
      
      const hasFolderAccess = folderRoles.some((role) => ['owner', 'contributor', 'viewer'].includes(role));
      
      if (hasCreator && hasContributor && hasReviewer && hasViewer) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Mixed Video Access:</strong> Has creator, contributor, reviewer, and viewer roles across different videos. Can fully control some videos, edit others, review some, and view others.
          </div>
        );
      }
      if (hasCreator && hasContributor) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Video Creator/Contributor:</strong> Can fully control some videos and edit assigned sections in others. Has access through explicit permissions and folder-level access.
          </div>
        );
      }
      if (hasCreator) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Video Creator:</strong> Has full control of these videos. Can edit all sections, manage permissions, and publish content.
          </div>
        );
      }
      if (hasContributor && hasReviewer && hasViewer) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Video Contributor/Reviewer/Viewer:</strong> Can edit assigned sections in some videos, review and comment on others, and view the rest. Has access through explicit permissions and folder-level access.
          </div>
        );
      }
      if (hasContributor && hasReviewer) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Video Contributor/Reviewer:</strong> Can edit assigned sections in some videos and review/comment on others. Has access through explicit permissions and folder-level access.
          </div>
        );
      }
      if (hasContributor && hasViewer) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Video Contributor/Viewer:</strong> Can edit assigned sections in some videos and view others. Has access through explicit permissions and folder-level access.
          </div>
        );
      }
      if (hasContributor) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Video Contributor:</strong> Can edit assigned sections in these videos. Has access through explicit permissions and folder-level access.
          </div>
        );
      }
      if (hasReviewer && hasViewer) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Video Reviewer/Viewer:</strong> Can review and comment on some videos and view others. Has access through explicit permissions and folder-level access.
          </div>
        );
      }
      if (hasReviewer) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Video Reviewer:</strong> Can review and comment on these videos. Has access through explicit permissions and folder-level access.
          </div>
        );
      }
      if (hasViewer) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Video Viewer:</strong> Can view these videos. Has read-only access through explicit permissions and folder-level access.
          </div>
        );
      }
      
      if (hasFolderAccess) {
        return (
          <div className={classes.infoBannerContent}>
            <strong>Video Access:</strong> Includes videos with explicit permissions and all videos accessible through folder-level permissions (shown above). Can view and interact with these videos based on assigned roles.
          </div>
        );
      }
      
      return null;
    };

    const permissionsContent = () => {
      if (isSelectedUserPermissionsLoading) {
        return (
          <div className={classes.permissionsLoading}>
            Loading permissions...
          </div>
        );
      }
      if (selectedUserPermissionsError) {
        return (
          <div className={classes.permissionsError}>
            {selectedUserPermissionsError?.response?.data?.message ||
              'Unable to load permissions.'}
          </div>
        );
      }
      return (
        <>
          {renderPermissionsGroup(
            'Organization access',
            organizationItems,
            RESOURCE_TYPES.ORGANIZATION,
            {
              highlightOwner: true,
              emptyLabel:
                'No organization access recorded for this user yet.',
              infoBanner: getOrgBannerText(),
            }
          )}
          {renderPermissionsGroup(
            'Folder access',
            folderItems,
            RESOURCE_TYPES.FOLDER,
            {
              emptyLabel:
                'This user does not currently have access to any folders.',
              infoBanner: getFolderBannerText(),
            },
            {
              page: folderPage,
              handleChange: handleFolderPageChange,
            }
          )}
          {renderPermissionsGroup(
            'Video access',
            meetingItems,
            RESOURCE_TYPES.MEETING,
            {
              emptyLabel:
                'This user does not currently have access to any videos.',
              infoBanner: getMeetingBannerText(),
            },
            {
              page: meetingPage,
              handleChange: handleMeetingPageChange,
            }
          )}
        </>
      );
    };

    return (
      <>
        <div className={classes.userInfoCard}>
          <div className={classes.userInfoHeader}>
            <div className={classes.userInfoContent}>
              <img
                src={avatar}
                alt={selectedUser.user?.name || 'User'}
                className={classes.userInfoAvatar}
              />
              <div className={classes.userInfoDetails}>
                <h2 className={classes.userInfoName}>
                  {selectedUser.user?.name || 'Unknown User'}
                </h2>
                {selectedUser.user?.email && (
                  <a
                    href={`mailto:${selectedUser.user.email}`}
                    className={classes.userInfoEmail}
                  >
                    {selectedUser.user.email}
                  </a>
                )}
                {selectedUser.user?.title && (
                  <span className={classes.userInfoTitle}>
                    {selectedUser.user.title}
                  </span>
                )}
              </div>
            </div>
            <div className={classes.userInfoRole}>
              {renderRoleBadge(
                selectedUser.role,
                selectedUser.role === ORG_ROLES.OWNER ? 'primary' : 'default'
              )}
            </div>
          </div>
        </div>

        {permissionsContent()}
      </>
    );
  };

  const getHeaderTitle = () => {
    if (isEditingUser) {
      return `Edit User: ${selectedUser?.user?.name || 'User'}`;
    }
    if (isViewingPermissions) {
      return `${selectedUser?.user?.name || 'User'} Permissions`;
    }
    return 'User Management';
  };

  const headerTitle = getHeaderTitle();

  if (!canManage) {
    return (
      <div className={classes.UserManagementPage}>
        <div className={classes.errorMessage}>
          You don&apos;t have permission to manage organization users. Only
          organization admins can access this page.
        </div>
      </div>
    );
  }

  return (
    <div className={classes.UserManagementPage}>
      <div className={classes.header}>
        <h1>{headerTitle}</h1>
        {isViewingPermissions || isEditingUser ? (
          <FilledButton onClick={handleBackToList}>
            ← Back to user list
          </FilledButton>
        ) : (
          <FilledButton
            onClick={() => {
              setIsPermissionManagerVisible(true);
            }}
          >
            + Add User
          </FilledButton>
        )}
      </div>

      {(() => {
        if (isEditingUser) {
          return renderEditView();
        }
        if (isViewingPermissions) {
          return renderPermissionsView();
        }
        return (
          <>
            <ReportTabs
              tabs={tabs}
              currentTab={currentTab}
              setCurrentTab={(tab) => {
                setCurrentTab(tab);
                setActivePage(1);
              }}
            />

            <div className={classes.searchContainer}>
              <SearchBar
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                placeholder="Search users..."
              />
            </div>

            <div className={classes.tableContainer}>
              {isLoading && (
                <div className={classes.loading}>Loading users...</div>
              )}

              {!isLoading && (
                <table className={classes.table}>
                  <thead>
                    <tr>
                      {tableHeaders.map((th) => (
                        <Th
                          key={th.field}
                          classes={classes}
                          sortField={sortField}
                          sortOrder={sortOrder}
                          setSortField={setSortField}
                          setSortOrder={setSortOrder}
                          field={th.field}
                          title={th.title}
                          noSort={th.noSort}
                        />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tabFilteredCount === 0 ? (
                      <tr className={classes.emptyRow}>
                        <td colSpan={tableHeaders.length}>{emptyMessage}</td>
                      </tr>
                    ) : (
                      paginatedUsers.map((permission) => (
                        <tr
                          key={permission.userId}
                          className={`${classes.userRow} ${
                            canManage ? classes.clickableRow : ''
                          }`}
                          onClick={
                            canManage
                              ? () => handleRowClick(permission.userId)
                              : undefined
                          }
                        >
                          <td data-label="Name" className={classes.nameCell}>
                            <div className={classes.userInfo}>
                              <img
                                src={
                                  getUserPhotoUrl(permission.user?.photoPath) ||
                                  userIcon
                                }
                                alt={permission.user?.name || 'User'}
                                className={classes.userAvatar}
                              />
                              <span>
                                {permission.user?.name || 'Unknown User'}
                              </span>
                            </div>
                          </td>
                          <td data-label="Email" className={classes.emailCell}>
                            {permission.user?.email || '-'}
                          </td>
                          <td data-label="Title" className={classes.titleCell}>
                            {permission.user?.title || '-'}
                          </td>
                          <td data-label="Role" className={classes.roleCell}>
                            {canManage && permission.userId !== currentUser?.id ? (
                              <select
                                className={classes.roleSelect}
                                value={permission.role}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(e) =>
                                  handleRoleChange(permission.userId, e.target.value)
                                }
                              >
                                {Object.values(ORG_ROLES)
                                  .filter((role) => role !== ORG_ROLES.MANAGER)
                                  .map((role) => (
                                    <option key={role} value={role}>
                                      {getRoleDisplayName(
                                        role,
                                        RESOURCE_TYPES.ORGANIZATION
                                      )}
                                    </option>
                                  ))}
                              </select>
                            ) : (
                              <span className={classes.roleBadge}>
                                {permission.role
                                  ? getRoleDisplayName(
                                      permission.role,
                                      RESOURCE_TYPES.ORGANIZATION
                                    )
                                  : '-'}
                              </span>
                            )}
                          </td>
                          <td data-label="Actions" className={classes.actionsCell}>
                            {canManage && permission.userId !== currentUser?.id && (
                              <div className={classes.actions}>
                                <FilledButton
                                  onClick={(event) => {
                                    handleEditClick(permission.userId, event);
                                  }}
                                  type="button"
                                >
                                  Edit
                                </FilledButton>
                                {permission.user?.isActive !== false ? (
                                  <>
                                    <FilledButton
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleResendInvitation(
                                          permission.userId,
                                          permission.user?.name,
                                          permission.user?.email
                                        );
                                      }}
                                      type="button"
                                      disabled={isResendingInvitation}
                                    >
                                      Resend Invitation
                                    </FilledButton>
                                    <FilledButton
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleRemoveUser(
                                          permission.userId,
                                          permission.user?.name || 'this user'
                                        );
                                      }}
                                      type="button"
                                    >
                                      Deactivate
                                    </FilledButton>
                                  </>
                                ) : (
                                  <FilledButton
                                    className={classes.reactivateButton}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      reactivateUser({ userId: permission.userId });
                                    }}
                                    type="button"
                                    disabled={isReactivating}
                                  >
                                    Reactivate
                                  </FilledButton>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {!isLoading && tabFilteredCount > itemsPerPage && (
              <div className={classes.paginationContainer}>
                <Pagination
                  activePage={activePage}
                  itemsCountPerPage={itemsPerPage}
                  totalItemsCount={tabFilteredCount}
                  pageRangeDisplayed={5}
                  onChange={(page) => setActivePage(page)}
                />
              </div>
            )}
          </>
        );
      })()}

      <PermissionManager
        show={isPermissionManagerVisible}
        onClose={() => {
          setIsPermissionManagerVisible(false);
        }}
        resourceId={organizationId}
        resourceType={RESOURCE_TYPES.ORGANIZATION}
        resourceName={currentUser?.Organization?.name || 'Organization'}
        user={currentUser}
        initialShowAddForm
        onResourceUpdate={() => {
          refetch();
        }}
      />
    </div>
  );
}
